window.AnatomyQuizSupport = {
  // v2 = inline consent notice under the email field; submitting the email is the
  // consent act. The Worker accepts v1 and v2 during rollout so a browser holding
  // cached JS does not 422, and records whichever version the customer actually saw.
  marketingConsentVersion: 'hair-consultation-email-submit-v2',

  _customerPrivacyReady: false,
  _customerPrivacyInitialized: false,
  _customerPrivacySettled: false,
  _customerPrivacySnapshot: null,
  _customerPrivacyWaiters: [],
  _deferredPrivacyTracks: [],
  _customerPrivacyLoadTimer: null,
  _globalCustomerPrivacy: null,

  _unknownCustomerPrivacy(source = 'unavailable') {
    return {
      analytics_processing_allowed: false,
      marketing_processing_allowed: false,
      sale_of_data_allowed: false,
      preferences_processing_allowed: false,
      analytics_state: 'unknown',
      marketing_state: 'unknown',
      sale_of_data_state: 'unknown',
      preferences_state: 'unknown',
      source,
    };
  },

  _readCustomerPrivacy() {
    if (this._globalCustomerPrivacy && typeof this._globalCustomerPrivacy.snapshot === 'function') {
      try {
        return this._normalizeGlobalCustomerPrivacy(this._globalCustomerPrivacy.snapshot());
      } catch (_error) {
        return this._unknownCustomerPrivacy('anatomy_customer_privacy_error');
      }
    }
    if (!this._customerPrivacyReady) return this._unknownCustomerPrivacy('not_ready');
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy) return this._unknownCustomerPrivacy('unavailable');

    const readAllowed = (method) => {
      if (typeof privacy[method] !== 'function') return 'unknown';
      const value = privacy[method]();
      if (value === true) return 'granted';
      if (value === false) return 'denied';
      return 'unknown';
    };

    try {
      const analyticsState = readAllowed('analyticsProcessingAllowed');
      const marketingState = readAllowed('marketingAllowed');
      const saleOfDataState = readAllowed('saleOfDataAllowed');
      const preferencesState = readAllowed('preferencesProcessingAllowed');
      return {
        analytics_processing_allowed: analyticsState === 'granted',
        marketing_processing_allowed: marketingState === 'granted',
        sale_of_data_allowed: saleOfDataState === 'granted',
        preferences_processing_allowed: preferencesState === 'granted',
        analytics_state: analyticsState,
        marketing_state: marketingState,
        sale_of_data_state: saleOfDataState,
        preferences_state: preferencesState,
        source: 'shopify_customer_privacy',
      };
    } catch (_error) {
      return this._unknownCustomerPrivacy('shopify_customer_privacy_error');
    }
  },

  _normalizeGlobalCustomerPrivacy(snapshot = {}) {
    const status = String(snapshot.status || 'loading');
    if (status !== 'ready') {
      return this._unknownCustomerPrivacy(
        status === 'error' ? 'anatomy_customer_privacy_error' : 'not_ready'
      );
    }
    const stateFor = (value) => value === true ? 'granted' : 'denied';
    return {
      analytics_processing_allowed: snapshot.analytics_processing_allowed === true,
      marketing_processing_allowed: snapshot.marketing_allowed === true,
      sale_of_data_allowed: snapshot.sale_of_data_allowed === true,
      preferences_processing_allowed: snapshot.preferences_processing_allowed === true,
      analytics_state: stateFor(snapshot.analytics_processing_allowed),
      marketing_state: stateFor(snapshot.marketing_allowed),
      sale_of_data_state: stateFor(snapshot.sale_of_data_allowed),
      preferences_state: stateFor(snapshot.preferences_processing_allowed),
      source: 'anatomy_customer_privacy',
    };
  },

  initCustomerPrivacy() {
    if (this._customerPrivacyInitialized) return;
    this._customerPrivacyInitialized = true;
    this._customerPrivacySnapshot = this._unknownCustomerPrivacy('not_ready');

    // Shopify documents this event as a change notification. Re-read the
    // authoritative Allowed() methods instead of trusting event detail fields.
    document.addEventListener('visitorConsentCollected', () => {
      // Never replay an event observed before a consent choice or change.
      this._deferredPrivacyTracks = [];
      this._customerPrivacySnapshot = this._readCustomerPrivacy();
    });

    const globalPrivacy = window.AnatomyCustomerPrivacy;
    if (
      globalPrivacy
      && typeof globalPrivacy.snapshot === 'function'
      && typeof globalPrivacy.subscribe === 'function'
    ) {
      this._globalCustomerPrivacy = globalPrivacy;
      try {
        globalPrivacy.subscribe((globalSnapshot) => {
          const snapshot = this._normalizeGlobalCustomerPrivacy(globalSnapshot);
          if (globalSnapshot && globalSnapshot.status === 'ready') {
            this._customerPrivacyReady = true;
            if (!this._customerPrivacySettled) this._settleCustomerPrivacy(snapshot);
            else this._customerPrivacySnapshot = snapshot;
            return;
          }
          this._customerPrivacyReady = false;
          if (globalSnapshot && globalSnapshot.status === 'error') {
            if (!this._customerPrivacySettled) this._settleCustomerPrivacy(snapshot);
            else this._customerPrivacySnapshot = snapshot;
            return;
          }
          this._customerPrivacySnapshot = snapshot;
        });
      } catch (_error) {
        this._globalCustomerPrivacy = null;
        this._settleCustomerPrivacy(this._unknownCustomerPrivacy('anatomy_customer_privacy_error'));
      }
      return;
    }

    const shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      this._settleCustomerPrivacy(this._unknownCustomerPrivacy('feature_unavailable'));
      return;
    }
    try {
      this._customerPrivacyLoadTimer = window.setTimeout(() => {
        this._customerPrivacyLoadTimer = null;
        this._customerPrivacyReady = false;
        this._settleCustomerPrivacy(this._unknownCustomerPrivacy('feature_load_timeout'));
      }, 1500);
      shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], (error) => {
        if (this._customerPrivacyLoadTimer) {
          window.clearTimeout(this._customerPrivacyLoadTimer);
          this._customerPrivacyLoadTimer = null;
        }
        if (error) {
          this._customerPrivacyReady = false;
          this._settleCustomerPrivacy(this._unknownCustomerPrivacy('feature_load_error'));
          return;
        }
        this._customerPrivacyReady = true;
        this._settleCustomerPrivacy(this._readCustomerPrivacy());
      });
    } catch (_error) {
      if (this._customerPrivacyLoadTimer) {
        window.clearTimeout(this._customerPrivacyLoadTimer);
        this._customerPrivacyLoadTimer = null;
      }
      this._customerPrivacyReady = false;
      this._settleCustomerPrivacy(this._unknownCustomerPrivacy('feature_load_error'));
    }
  },

  _settleCustomerPrivacy(snapshot) {
    this._customerPrivacySnapshot = snapshot;
    this._customerPrivacySettled = true;
    const waiters = this._customerPrivacyWaiters.splice(0);
    waiters.forEach((resolve) => {
      try { resolve({ ...snapshot }); } catch (_error) {}
    });

    // Defer only across the feature-load race. Once Shopify has supplied an
    // authoritative state, denied/unknown events are dropped and never replayed
    // after a later consent choice.
    const deferred = this._deferredPrivacyTracks.splice(0);
    deferred.forEach(({ eventName, data, component, options }) => {
      if (component && component.isConnected === false) return;
      this.track(eventName, data, component, options);
    });
  },

  whenCustomerPrivacyReady() {
    if (!this._customerPrivacyInitialized) this.initCustomerPrivacy();
    if (this._customerPrivacySettled) {
      return Promise.resolve(this.customerPrivacySnapshot());
    }
    return new Promise((resolve) => this._customerPrivacyWaiters.push(resolve));
  },

  customerPrivacySnapshot() {
    if (!this._customerPrivacyInitialized) this.initCustomerPrivacy();
    return { ...(this._customerPrivacySnapshot || this._unknownCustomerPrivacy()) };
  },

  analyticsAllowed(snapshot = this.customerPrivacySnapshot()) {
    return snapshot.analytics_processing_allowed === true;
  },

  advertisingAllowed(snapshot = this.customerPrivacySnapshot()) {
    return snapshot.marketing_processing_allowed === true
      && snapshot.sale_of_data_allowed === true;
  },

  browserIdentityAllowed(snapshot = this.customerPrivacySnapshot()) {
    return snapshot.analytics_processing_allowed === true
      && snapshot.marketing_processing_allowed === true;
  },

  _resultContextExpiry(component) {
    const context = component && component.resultContext;
    if (!context || typeof context !== 'object') return null;
    const createdAt = Number(context.ts);
    const now = Date.now();
    if (!Number.isFinite(createdAt) || createdAt <= 0 || createdAt > now + 60000) return 0;
    const oneHourExpiresAt = createdAt + 60 * 60 * 1000;
    if (Number(context.version) >= 2) {
      const sessionExpiresAt = Number(context.sessionExpiresAt);
      return Number.isFinite(sessionExpiresAt) && sessionExpiresAt > 0
        ? Math.min(sessionExpiresAt, oneHourExpiresAt)
        : 0;
    }
    const tokenPrefix = String(context.sessionToken || '').split('.', 1)[0];
    const tokenExpiresAt = /^\d{10,16}$/.test(tokenPrefix) ? Number(tokenPrefix) : 0;
    return Number.isFinite(tokenExpiresAt) && tokenExpiresAt > 0
      ? Math.min(tokenExpiresAt, oneHourExpiresAt)
      : oneHourExpiresAt;
  },

  _resultContextExpiredError() {
    const error = new Error('This consultation session has expired. Retake the consultation to continue.');
    error.name = 'QuizResultContextExpiredError';
    error.code = 'result_context_expired';
    error.status = 401;
    error.retryable = false;
    return error;
  },

  async ensureSession(component, forceRefresh = false) {
    if (!component || !component.config || !component.config.workerUrl || !component.sessionId) {
      throw new Error('Consultation session is not configured');
    }
    const resultContextExpiry = this._resultContextExpiry(component);
    if (resultContextExpiry !== null) {
      if (
        component.resultContextExpired === true
        || !resultContextExpiry
        || Date.now() >= resultContextExpiry
        || !component.sessionToken
        || forceRefresh
      ) {
        throw this._resultContextExpiredError();
      }
      // Result-page credentials are bounded by the context that created them.
      // Never mint or adopt a later token, even inside the normal 30s refresh window.
      return component.sessionToken;
    }
    if (!forceRefresh && component.sessionToken && component.sessionExpiresAt > Date.now() + 30000) {
      return component.sessionToken;
    }
    if (!forceRefresh && component._sessionPromise) return component._sessionPromise;

    component._sessionPromise = fetch(`${component.config.workerUrl}/api/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: component.sessionId })
    }).then(async (response) => {
      if (!response.ok) {
        const error = new Error(`Session API ${response.status}`);
        error.status = response.status;
        error.retryAfterMs = Math.max(0, Number(response.headers.get('Retry-After')) || 0) * 1000;
        throw error;
      }
      const session = await response.json();
      component.sessionToken = session.token;
      component.sessionExpiresAt = Number(session.expires_at) || 0;
      return component.sessionToken;
    }).finally(() => {
      component._sessionPromise = null;
    });
    return component._sessionPromise;
  },

  async workerFetch(component, path, options = {}, retry = true) {
    const resultContextExpiry = this._resultContextExpiry(component);
    if (resultContextExpiry !== null && (!resultContextExpiry || Date.now() >= resultContextExpiry)) {
      throw this._resultContextExpiredError();
    }
    const token = await this.ensureSession(component);
    if (resultContextExpiry !== null && Date.now() >= resultContextExpiry) {
      throw this._resultContextExpiredError();
    }
    const headers = new Headers(options.headers || {});
    headers.set('X-Anatomy-Session', token);
    const response = await fetch(`${component.config.workerUrl}${path}`, { ...options, headers });
    if (resultContextExpiry !== null && Date.now() >= resultContextExpiry) {
      throw this._resultContextExpiredError();
    }
    if (response.status === 401 && retry) {
      if (resultContextExpiry !== null) throw this._resultContextExpiredError();
      component.sessionToken = '';
      component.sessionExpiresAt = 0;
      await this.ensureSession(component, true);
      return this.workerFetch(component, path, options, false);
    }
    return response;
  },

  async consumeEventStream(response, onEvent) {
    if (!response || !response.body || typeof response.body.getReader !== 'function') {
      throw new Error('Streaming response body is unavailable');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const drain = (flush = false) => {
      buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        if (!frame || frame.charAt(0) === ':') continue;

        let eventName = 'message';
        const dataLines = [];
        frame.split('\n').forEach((line) => {
          if (line.indexOf('event:') === 0) eventName = line.slice(6).trim();
          if (line.indexOf('data:') === 0) dataLines.push(line.slice(5).trimStart());
        });
        onEvent(eventName, dataLines.join('\n'));
      }

      if (flush && buffer.trim() && buffer.charAt(0) !== ':') {
        const trailing = buffer;
        buffer = '';
        let eventName = 'message';
        const dataLines = [];
        trailing.split('\n').forEach((line) => {
          if (line.indexOf('event:') === 0) eventName = line.slice(6).trim();
          if (line.indexOf('data:') === 0) dataLines.push(line.slice(5).trimStart());
        });
        if (dataLines.length) onEvent(eventName, dataLines.join('\n'));
      }
    };

    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });
      drain();
    }
    buffer += decoder.decode();
    drain(true);
  },

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  },

  escapeAttr(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  },

  renderDebug(component, data) {
    const panel = component.querySelector('[data-quiz-debug]');
    if (!panel || !component.config.debug) {
      if (panel) panel.hidden = true;
      return;
    }

    const debugData = { ...data };
    const meta = debugData._debug || {};
    const timing = meta.duration
      ? `<strong class="quiz-debug__timing">${this.escapeHtml(meta.duration)}</strong>`
      : '';
    const tokens = meta.tokens
      ? ` | In: ${meta.tokens.input_tokens || '?'} Out: ${meta.tokens.output_tokens || '?'}${meta.tokens.output_tokens_details?.reasoning_tokens ? ` (reasoning: ${meta.tokens.output_tokens_details.reasoning_tokens})` : ''}`
      : '';

    panel.hidden = false;
    panel.innerHTML =
      `<details class="quiz-debug__details" open>` +
      `<summary class="quiz-debug__summary">Debug: Q${component.questionCount + 1} ${timing ? `| ${timing}` : ''}</summary>` +
      (meta.error ? `<div class="quiz-debug__meta"><strong class="quiz-debug__error">ERROR: ${this.escapeHtml(meta.error)}</strong></div>` : '') +
      `<div class="quiz-debug__meta">${this.escapeHtml(meta.provider || '?')} | ${this.escapeHtml(meta.model || '?')} | effort: ${this.escapeHtml(meta.effort || '?')} | turn ${meta.turn ?? '?'}${meta.raw_length ? ` | ${meta.raw_length} chars` : ''}${tokens}</div>` +
      `<pre class="quiz-debug__json">${this.escapeHtml(JSON.stringify(debugData, null, 2))}</pre>` +
      `</details>`;
  },

  identifyKlaviyo(component, email) {
    if (component && component.qaMode) return;
    if (!this.browserIdentityAllowed()) return;
    const identity = window.anatomyKlaviyoIdentitySync;
    if (!identity || typeof identity.identify !== 'function') return false;
    return identity.identify(email, {
      reason: 'quiz_email_capture',
      emailConsent: true,
      extra: {
        anatomy_quiz_session_id: component?.sessionId || '',
        quiz_completed: true,
        quiz_total_questions: component?.questionCount || component?.conversationHistory?.length || 0,
      },
    });
  },

  async completeEmail(component, email, options = {}) {
    if (component && component.qaMode) return { ok: true, qa_suppressed: true };
    const captureContext = options.captureContext || 'email_gate';
    const leadEventId = options.leadEventId || '';

    if (!component.config.workerUrl) throw new Error('The consultation email service is unavailable. Please try again.');

    try {
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? decodeURIComponent(match[1]) : '';
      };
      const firstTurn = (component.conversationHistory && component.conversationHistory[0]) || {};
      const contextSummary = (component.resultContext && component.resultContext.summary) || {};
      const primaryConcern = String(
        contextSummary.primaryConcern
          || (firstTurn.input_type === 'freeform' ? 'freeform' : (firstTurn.answer || firstTurn.answer_label))
          || 'unspecified'
      ).replace(/\s+/g, ' ').trim().slice(0, 160) || 'unspecified';
      const totalQuestions = Math.max(1, Math.min(6, Number(
        component.questionCount || contextSummary.totalQuestions || (component.conversationHistory || []).length
      ) || 1));
      const customerPrivacy = this.customerPrivacySnapshot();
      const emailPayload = {
        contract_version: 2,
        email,
        session_id: component.sessionId,
        anatomy_visitor_id: this.analyticsAllowed(customerPrivacy) && component.visitorId ? component.visitorId() : '',
        primary_concern: primaryConcern,
        total_questions: totalQuestions,
        subscribe_marketing: true,
        marketing_consent_version: this.marketingConsentVersion,
        wait_for_completion: true,
        capture_context: captureContext,
        customer_privacy: customerPrivacy,
      };
      if (this.advertisingAllowed(customerPrivacy)) {
        emailPayload.fb = {
          event_id: leadEventId,
          event_source_url: window.location.href,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
        };
      }

      // keepalive: the quiz navigates to the results page right after submit —
      // without it the browser aborts this in-flight call ("Failed to fetch")
      // even though the worker usually still receives it.
      const response = await this.workerFetch(component, '/api/email', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload),
      });
      const data = await response.json().catch(() => ({}));
      const state = String(data.status || data.completion_status || '').toLowerCase();
      const deferred = response.status === 202
        || data.deferred === true
        || ['accepted', 'deferred', 'pending', 'queued', 'running'].includes(state);
      if (!response.ok || deferred || data.success === false) {
        const fallback = deferred
          ? 'Your consultation is still being prepared. Please try again in a moment.'
          : `Email API ${response.status}`;
        const error = new Error(String(data.error || fallback).slice(0, 320));
        error.status = response.status;
        error.retryable = true;
        error.deferred = deferred;
        throw error;
      }
      if (this.browserIdentityAllowed()) {
        try {
          window.dispatchEvent(new CustomEvent('anatomy:identify-email', {
            detail: {
              email,
              reason: captureContext === 'results_save' ? 'quiz_results_save' : 'quiz_email_capture',
              extra: {
                anatomy_quiz_session_id: component.sessionId,
                quiz_completed: true,
                quiz_primary_concern: primaryConcern,
                quiz_total_questions: totalQuestions
              }
            }
          }));
        } catch (err) {}
      }
      try {
        sessionStorage.setItem('anatomy:quiz-identity-complete', component.sessionId);
      } catch (err) {}
      return data;
    } catch (err) {
      throw err;
    }
  },

  async sendEmail(component, email, leadEventId) {
    return this.completeEmail(component, email, {
      leadEventId,
      captureContext: 'email_gate',
      waitForCompletion: true,
    });
  },

  QUIZ_EVENT_MAP: {
    QuizStarted:          { ga: 'quiz_started',           ph: 'quiz_started',           pixel: 'custom',   capi: 'QuizStarted' },
    QuizProgress:         { ga: 'quiz_question_answered', ph: null,                     pixel: 'none',     capi: null },
    QuizCompleted:        { ga: 'quiz_completed',         ph: 'quiz_completed',         pixel: 'custom',   capi: 'QuizCompleted' },
    QuizAssessmentViewed: { ga: 'quiz_assessment_viewed', ph: 'quiz_assessment_viewed', pixel: 'custom',   capi: 'QuizAssessmentViewed' },
    QuizResultsViewed:    { ga: 'quiz_assessment_viewed', ph: 'quiz_assessment_viewed', pixel: 'custom',   capi: 'QuizAssessmentViewed' }, // legacy alias
    QuizCTAClicked:       { ga: 'quiz_cta_clicked',       ph: 'quiz_cta_clicked',       pixel: 'custom',   capi: null },
    Lead:                 { ga: 'generate_lead',          ph: null,                     pixel: 'standard', capi: null } // ph+capi handled by /api/email
  },

  _cookie(n) { const m = document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')); return m ? decodeURIComponent(m[1]) : ''; },

  posthogSessionId() {
    try {
      return window.posthog && typeof window.posthog.get_session_id === 'function'
        ? window.posthog.get_session_id() || ''
        : '';
    } catch (_error) {
      return '';
    }
  },

  _sanitizeTrackData(data = {}) {
    const safe = {};
    if (['Hair Consultation', 'Hair Consultation Results Save'].includes(data.content_name)) {
      safe.content_name = data.content_name;
    }
    const categoricalStrings = new Set([
      'severity', 'damage_severity', 'input_type', 'render_path', 'assessment_source',
      'currency', 'question_id', 'dimension', 'input_mode', 'primary_concern'
    ]);
    categoricalStrings.forEach((key) => {
      const value = data[key];
      if (typeof value === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(value)) safe[key] = value;
    });
    ['total_questions', 'question_number', 'step_index', 'value'].forEach((key) => {
      const value = Number(data[key]);
      if (Number.isFinite(value)) safe[key] = value;
    });
    if (typeof data._fbEventId === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(data._fbEventId)) {
      safe._fbEventId = data._fbEventId;
    }
    if (data._fbStandard === true) safe._fbStandard = true;
    return safe;
  },

  _trackingComponentSnapshot(component) {
    if (!component) return null;
    return {
      qaMode: component.qaMode === true,
      config: {
        workerUrl: String(component.config?.workerUrl || ''),
        quizSource: String(component.config?.quizSource || ''),
      },
      sessionId: String(component.sessionId || ''),
      sessionToken: String(component.sessionToken || ''),
      sessionExpiresAt: Number(component.sessionExpiresAt) || 0,
      resultContext: component.resultContext && typeof component.resultContext === 'object' ? {
        version: Number(component.resultContext.version) || 0,
        ts: Number(component.resultContext.ts) || 0,
        sessionExpiresAt: Number(component.resultContext.sessionExpiresAt) || 0,
        sessionToken: String(component.resultContext.sessionToken || ''),
      } : null,
      resultContextExpired: component.resultContextExpired === true,
      _sessionPromise: null,
      dataset: { sectionId: String(component.dataset?.sectionId || '') },
      useAdaptiveBank: component.useAdaptiveBank === true,
      _legacyChipMode: component._legacyChipMode === true,
      visitorId() {
        if (!window.AnatomyQuizSupport.analyticsAllowed()) return '';
        try {
          const stored = window.localStorage.getItem('anatomy_visitor_id_v1');
          if (stored) return stored;
        } catch (_error) {}
        const match = document.cookie.match(/(?:^|; )anatomy_visitor_id=([^;]*)/);
        return match ? decodeURIComponent(match[1]) : '';
      },
    };
  },

  track(eventName, data = {}, component = null, options = {}) {
    if (component && component.qaMode) return { emitted: false, qa_suppressed: true };
    const map = this.QUIZ_EVENT_MAP[eventName] || { ga: data._gaEvent || eventName, ph: null, pixel: 'custom', capi: null };
    const sanitizedData = this._sanitizeTrackData(data);
    const { _fbStandard, _fbEventId, _gaEvent, _phProps, ...payload } = sanitizedData;
    const customerPrivacy = this.customerPrivacySnapshot();
    if (!this._customerPrivacySettled && customerPrivacy.source === 'not_ready') {
      if (this._deferredPrivacyTracks.length < 30) {
        this._deferredPrivacyTracks.push({
          eventName,
          data: sanitizedData,
          component: this._trackingComponentSnapshot(component),
          options: { dispatchAssessmentView: options.dispatchAssessmentView === true },
        });
      }
      return {
        emitted: false,
        privacy_loading: true,
        customer_privacy: customerPrivacy,
      };
    }
    const analyticsAllowed = this.analyticsAllowed(customerPrivacy);
    const advertisingAllowed = this.advertisingAllowed(customerPrivacy);
    const shouldMirrorCapi = !!map.capi && advertisingAllowed;
    const shouldMirrorPosthog = !!map.ph && analyticsAllowed;
    const shouldMirror = shouldMirrorCapi || shouldMirrorPosthog;
    const eventId = (advertisingAllowed || shouldMirrorPosthog)
      ? (_fbEventId || (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`))
      : null;
    let analyticsEmitted = false;
    let advertisingEmitted = false;
    let workerEmitted = false;
    const posthogSessionId = analyticsAllowed ? this.posthogSessionId() : '';

    // standard bundle (when component is passed)
    const std = component ? {
      quiz_source: (component.config && component.config.quizSource) || '',
      quiz_session_id: component.sessionId,
      anatomy_visitor_id: analyticsAllowed && component.visitorId ? component.visitorId() : '',
      quiz_mode: component.useAdaptiveBank ? (component._legacyChipMode ? 'legacy_chip' : 'adaptive_bank') : 'legacy_question',
      section_id: (component.dataset && component.dataset.sectionId) || ''
    } : {};

    // Pixel
    if (advertisingAllowed && typeof window.fbq === 'function') {
      if (map.pixel === 'standard') window.fbq('track', eventName, payload, eventId ? { eventID: eventId } : undefined);
      else if (map.pixel === 'custom') window.fbq('trackCustom', eventName, payload, eventId ? { eventID: eventId } : undefined);
      if (map.pixel !== 'none') advertisingEmitted = true;
    }
    // GA4
    if (analyticsAllowed && typeof window.gtag === 'function') {
      window.gtag('event', map.ga, payload);
      analyticsEmitted = true;
    }
    // PostHog (client-owned events only)
    if (analyticsAllowed && map.ph && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(map.ph, Object.assign({}, std, payload, {
        $insert_id: eventId || undefined,
        $session_id: posthogSessionId || undefined,
      }));
      analyticsEmitted = true;
    }
    // Worker mirrors consent-eligible destinations. PostHog receives the same
    // insert id as the browser capture so its client/server copies deduplicate.
    if (shouldMirror && eventId && component && component.config && component.config.workerUrl) {
      try {
        const trackPayload = {
          event_id: eventId,
          session_id: component.sessionId,
          anatomy_visitor_id: std.anatomy_visitor_id,
          posthog_session_id: posthogSessionId,
          customer_privacy: customerPrivacy,
          custom_data: {
            content_name: 'Hair Consultation', quiz_source: std.quiz_source,
            severity: payload.severity || payload.damage_severity || undefined
          }
        };
        if (shouldMirrorCapi) {
          trackPayload.event_name = map.capi;
          trackPayload.fb = {
            fbp: this._cookie('_fbp'),
            fbc: this._cookie('_fbc'),
            event_source_url: location.href,
          };
        }
        if (shouldMirrorPosthog) {
          trackPayload.posthog = {
            event: map.ph,
            props: Object.assign({}, std, payload, {
              $insert_id: eventId,
              $session_id: posthogSessionId || undefined,
            }),
          };
        }
        this.workerFetch(component, '/api/track', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
          body: JSON.stringify(trackPayload)
        }).catch(() => {});
        workerEmitted = true;
        if (shouldMirrorPosthog) analyticsEmitted = true;
      } catch (e) {}
    }
    const outcome = {
      emitted: analyticsEmitted || advertisingEmitted || workerEmitted,
      analytics_emitted: analyticsEmitted,
      advertising_emitted: advertisingEmitted,
      worker_emitted: workerEmitted,
      customer_privacy: customerPrivacy,
    };
    if (options.dispatchAssessmentView === true && analyticsEmitted) {
      try {
        window.dispatchEvent(new CustomEvent('anatomy:quiz-assessment-viewed', { detail: payload }));
      } catch (_error) {}
    }
    return outcome;
  },

  normalizeResultForStorage(result) {
    const source = result && typeof result === 'object' ? result : this.getFallbackResult();
    const normalized = {};
    [
      'assessment_version', 'damage_severity', 'diagnostic_scope', 'scope_note',
      'damage_profile_headline', 'damage_profile_summary', 'what_is_happening',
      'key_insight', 'why_previous_products_underperformed',
      'how_anatomy_works_for_you', 'purchase_nudge'
    ].forEach((key) => {
      if (typeof source[key] === 'string') normalized[key] = source[key].slice(0, 4000);
    });
    ['in_scope', 'commerce_allowed', 'assessment_unavailable'].forEach((key) => {
      if (typeof source[key] === 'boolean') normalized[key] = source[key];
    });
    if (Number.isFinite(Number(source.schema_version))) normalized.schema_version = Number(source.schema_version);
    if (source.reconstruction_protocol && typeof source.reconstruction_protocol === 'object') {
      normalized.reconstruction_protocol = {};
      ['entry_point', 'intensive_phase', 'maintenance_phase', 'expected_timeline', 'personalized_note'].forEach((key) => {
        const value = source.reconstruction_protocol[key];
        if (typeof value === 'string') normalized.reconstruction_protocol[key] = value.slice(0, 2000);
      });
    }
    if (Array.isArray(source.clinical_proof)) {
      normalized.clinical_proof = source.clinical_proof.slice(0, 4).map((proof) => {
        if (typeof proof === 'string') return proof.slice(0, 1000);
        if (!proof || typeof proof !== 'object') return null;
        return {
          stat: typeof proof.stat === 'string' ? proof.stat.slice(0, 120) : '',
          context: typeof proof.context === 'string' ? proof.context.slice(0, 1000) : ''
        };
      }).filter(Boolean);
    }
    if (source._meta && Number.isFinite(Number(source._meta.total_questions))) {
      normalized._meta = { total_questions: Math.max(0, Math.min(6, Number(source._meta.total_questions))) };
    }
    return normalized;
  },

  persistResultContext(component, identitySaved = false, result = null) {
    if (!component || !component.sessionId) return null;
    const normalizedResult = this.normalizeResultForStorage(result || component.currentResult || {});
    const previousSummary = (component.resultContext && component.resultContext.summary) || {};
    const firstTurn = (component.conversationHistory && component.conversationHistory[0]) || {};
    const normalizedConcern = firstTurn.input_type === 'freeform'
      ? 'freeform'
      : String(firstTurn.answer || previousSummary.primaryConcern || 'unspecified')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160) || 'unspecified';
    const hasSavedIdentity = identitySaved === true || component.resultContext?.identitySaved === true;
    const summary = {
      version: 2,
      sessionId: component.sessionId,
      completedAt: previousSummary.completedAt || new Date().toISOString(),
      identitySaved: hasSavedIdentity,
      primaryConcern: previousSummary.primaryConcern || normalizedConcern,
      totalQuestions: Math.max(1, Math.min(6, Number(
        component.questionCount || previousSummary.totalQuestions || (component.conversationHistory || []).length
      ) || 1)),
      damageSeverity: normalizedResult.damage_severity || previousSummary.damageSeverity || '',
      diagnosticScope: normalizedResult.diagnostic_scope || previousSummary.diagnosticScope || '',
      commerceAllowed: normalizedResult.commerce_allowed === true,
      entryPoint: normalizedResult.reconstruction_protocol?.entry_point || previousSummary.entryPoint || ''
    };
    const context = {
      version: 2,
      sessionId: component.sessionId,
      sessionToken: component.sessionToken || '',
      sessionExpiresAt: Number(component.sessionExpiresAt) || 0,
      anatomyVisitorId: component.visitorId ? component.visitorId() : (component.anatomyVisitorId || ''),
      identitySaved: hasSavedIdentity,
      summary,
      quizMode: component.useAdaptiveBank ? (component._legacyChipMode ? 'legacy_chip' : 'adaptive_bank') : 'legacy_question',
      totalQuestions: summary.totalQuestions,
      ts: Date.now(),
    };
    try {
      sessionStorage.setItem('anatomy:quiz-result-context', JSON.stringify(context));
      sessionStorage.setItem('anatomy:quiz-completion-v2', JSON.stringify(summary));
      if (hasSavedIdentity) sessionStorage.setItem('anatomy:quiz-identity-complete', component.sessionId);
    } catch (err) {
      console.error('Quiz result context storage failed:', err);
    }
    return context;
  },

  readResultContext() {
    try {
      const raw = sessionStorage.getItem('anatomy:quiz-result-context');
      if (!raw) return { context: null, status: 'missing' };
      const context = JSON.parse(raw);
      const createdAt = Number(context && context.ts);
      const now = Date.now();
      if (!context || !context.sessionId || !Number.isFinite(createdAt) || createdAt <= 0 || createdAt > now + 60000) {
        sessionStorage.removeItem('anatomy:quiz-result-context');
        return { context: null, status: 'invalid' };
      }
      if (Object.prototype.hasOwnProperty.call(context, 'email')) {
        delete context.email;
        try { sessionStorage.setItem('anatomy:quiz-result-context', JSON.stringify(context)); } catch (_storageError) {}
      }

      if (Number(context.version) >= 2) {
        const sessionExpiresAt = Number(context.sessionExpiresAt);
        const oneHourExpiresAt = createdAt + 60 * 60 * 1000;
        const expiresAt = Number.isFinite(sessionExpiresAt) && sessionExpiresAt > 0
          ? Math.min(sessionExpiresAt, oneHourExpiresAt)
          : 0;
        if (!expiresAt || now >= expiresAt) {
          sessionStorage.removeItem('anatomy:quiz-result-context');
          return { context: null, status: 'expired', expiresAt };
        }
        return { context, status: 'valid', expiresAt };
      }

      const tokenExpiryPrefix = String(context.sessionToken || '').split('.', 1)[0];
      const tokenExpiresAt = /^\d{10,16}$/.test(tokenExpiryPrefix)
        ? Number(tokenExpiryPrefix)
        : 0;
      const oneHourExpiresAt = createdAt + 60 * 60 * 1000;
      const expiresAt = Number.isFinite(tokenExpiresAt) && tokenExpiresAt > 0
        ? Math.min(tokenExpiresAt, oneHourExpiresAt)
        : oneHourExpiresAt;
      if (now >= expiresAt) {
        sessionStorage.removeItem('anatomy:quiz-result-context');
        return { context: null, status: 'expired', expiresAt };
      }
      return { context, status: 'valid', expiresAt };
    } catch (_error) {
      try { sessionStorage.removeItem('anatomy:quiz-result-context'); } catch (_storageError) {}
      return { context: null, status: 'invalid' };
    }
  },

  getResultContext() {
    return this.readResultContext().context;
  },

  replaceCompletionState(component, result, identitySaved = false) {
    const normalizedResult = this.normalizeResultForStorage(result);
    try {
      [
        'anatomy:quiz-answers',
        'anatomy:quiz-prelim',
        'anatomy:quiz-stream-started',
        'anatomy:quiz-progress-v1',
        'anatomy:quiz-result-context',
        'anatomy:quiz-results'
      ].forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.setItem('anatomy:quiz-results', JSON.stringify(normalizedResult));
    } catch (err) {
      console.error('Quiz result storage failed:', err);
    }
    const context = this.persistResultContext(component, identitySaved === true, normalizedResult);
    if (component) {
      component.conversationHistory = [];
      component.currentResult = normalizedResult;
    }
    return { result: normalizedResult, context, summary: context?.summary || null };
  },

  storeAndRedirect(component, result, email) {
    this.replaceCompletionState(component, result, !!email);
    this.goToResults(component);
  },

  goToResults(component) {
    let url = component.config.resultsPageUrl || '/pages/hair-consultation-results';
    if (component.qaMode) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('anatomy_qa', '1');
        url = parsed.pathname + parsed.search + parsed.hash;
      } catch (e) { /* keep the configured URL */ }
    }
    window.location.href = url;
  },

  getFallbackResult() {
    return {
      assessment_unavailable: true,
      in_scope: false,
      commerce_allowed: false,
      scope_note: 'No assessment was completed, so no product recommendation has been made.',
      damage_profile_headline: 'We could not complete your profile yet',
      damage_profile_summary: 'Your answers are saved for this session, but the assessment service did not return a complete result. We will not replace it with a generic damage label or product recommendation.',
      what_is_happening: 'No conclusion has been drawn from an incomplete assessment response.',
      key_insight: 'Please retry the consultation when the assessment service is available.',
      how_anatomy_works_for_you: '',
      reconstruction_protocol: {
        entry_point: 'assessment_not_completed',
        intensive_phase: 'No reconstruction recommendation has been generated.',
        maintenance_phase: '',
        expected_timeline: '',
      },
      clinical_proof: null,
      purchase_nudge: null,
    };
  },
};

window.AnatomyQuizSupport.initCustomerPrivacy();
